import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "../../App";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import {
  Sparkles, RefreshCw, ExternalLink, AlertTriangle, MessageSquare, Mic,
  CheckCircle2, Clock, User, ArrowUpRight, Activity,
} from "lucide-react";

/**
 * Program-level Fathom intelligence panel.
 * Renders inside ProgramDetail as a "Calls" tab. Two sub-views:
 *   • Intelligence Overview — Claude-aggregated insight across all linked calls
 *   • Calls Timeline — chronological list of every linked call
 */
export function ProgramCallsTab({ programId }) {
  const [calls, setCalls] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (forceRefresh = false) => {
    setLoading(true);
    // Load independently — an AI-overview failure must never hide the calls list
    const [callsR, ovR] = await Promise.allSettled([
      apiClient.get(`/fathom/programs/${programId}/calls`),
      apiClient.get(`/fathom/programs/${programId}/intelligence${forceRefresh ? "?force_refresh=true" : ""}`),
    ]);
    if (callsR.status === "fulfilled") setCalls(callsR.value.data.items || []);
    if (ovR.status === "fulfilled") setOverview(ovR.value.data);
    else setOverview(null);
    setLoading(false);
  };

  useEffect(() => { if (programId) load(); /* eslint-disable-next-line */ }, [programId]);

  const refresh = async () => { setRefreshing(true); await load(true); setRefreshing(false); };

  if (loading) return <Card className="p-6 text-sm text-slate-500">Loading call intelligence…</Card>;

  const hasCalls = calls.length > 0;

  return (
    <div className="space-y-4" data-testid="program-calls-tab">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-violet-600" />
          <h2 className="text-lg font-semibold text-slate-900 tracking-tight">Call Intelligence</h2>
          <Badge variant="outline" className="text-[10px]">{calls.length} linked call{calls.length === 1 ? "" : "s"}</Badge>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/fathom/calls">
            <Button variant="outline" size="sm">View all calls <ArrowUpRight className="w-3 h-3 ml-1" /></Button>
          </Link>
          {hasCalls && (
            <Button variant="outline" size="sm" onClick={refresh} disabled={refreshing} data-testid="refresh-intelligence-btn">
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh overview
            </Button>
          )}
        </div>
      </div>

      {!hasCalls ? (
        <Card className="p-10 text-center text-slate-500" data-testid="program-calls-empty">
          <Mic className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          <p className="text-sm mb-1">No Fathom calls are linked to this project yet.</p>
          <p className="text-xs text-slate-400 mb-3">Calls auto-link via participant email match. You can also link manually from the call detail page.</p>
          <Link to="/fathom/calls">
            <Button size="sm" variant="outline">Browse all calls</Button>
          </Link>
        </Card>
      ) : (
        <Tabs defaultValue="overview">
          <TabsList>
            <TabsTrigger value="overview" data-testid="program-calls-overview-tab">Intelligence Overview</TabsTrigger>
            <TabsTrigger value="timeline" data-testid="program-calls-timeline-tab">Calls Timeline</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-4 space-y-3">
            {overview?.empty ? (
              <p className="text-sm text-slate-500">No AI-extracted intelligence available.</p>
            ) : (
              <>
                {overview?.summary && (
                  <Card className="p-5 bg-violet-50/60 border-violet-200">
                    <div className="text-[10px] uppercase tracking-[0.16em] text-violet-700 mb-2 flex items-center gap-1.5">
                      <Sparkles className="w-3 h-3" />
                      {overview.source === "fathom" ? "Latest call summary · from Fathom" : "Executive snapshot"}
                    </div>
                    <p className="text-sm text-slate-800 leading-relaxed">{overview.summary}</p>
                    <div className="mt-3 text-[11px] text-slate-500">
                      {overview.source === "fathom" ? (
                        <>
                          {overview.latest_call_title}
                          {overview.latest_call_at && ` · ${new Date(overview.latest_call_at).toLocaleDateString()}`}
                          {" · "}Fathom's own summary across {overview.call_count} linked call{overview.call_count === 1 ? "" : "s"}
                        </>
                      ) : (
                        <>Aggregated across {overview.call_count} call{overview.call_count === 1 ? "" : "s"} · last generated {overview.generated_at && new Date(overview.generated_at).toLocaleString()}</>
                      )}
                    </div>
                  </Card>
                )}

                {overview?.source === "fathom" && (
                  <div className="flex items-start gap-2 text-[11px] text-slate-500 px-1">
                    <Activity className="w-3.5 h-3.5 flex-shrink-0 mt-px" />
                    <span>
                      Showing Fathom's own AI output — summaries and action items it already generated.
                      Cross-call analysis (scope, risks, commitments) is off; enable it in Settings → Fathom.
                    </span>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <IntelSection label="What the client wants" items={overview?.what_client_wants} />
                  <IntelSection label="Main project goals" items={overview?.main_goals} />
                  <IntelSection label="Current scope discussed" items={overview?.current_scope} />
                  <IntelSection label="Open questions" items={overview?.open_questions} />
                  <IntelSection label="Concerns / objections" items={overview?.concerns_objections} />
                  <IntelSection label="TRP commitments made" items={overview?.trp_promises} accent="amber" />
                  <IntelSection label="Pricing mentions" items={overview?.pricing_mentions} />
                  <IntelSection label="Timeline expectations" items={overview?.timeline_expectations} />
                  <IntelSection label="Key stakeholders" items={overview?.key_stakeholders} />
                  <IntelSection
                    label={overview?.source === "fathom"
                      ? `Open action items${overview.completed_action_items_count ? ` · ${overview.completed_action_items_count} completed` : ""}`
                      : "Next steps"}
                    items={overview?.next_steps}
                  />
                </div>

                {overview?.risk_flags?.length > 0 && (
                  <Card className="p-4 border-rose-200 bg-rose-50/40" data-testid="program-risk-flags">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-3.5 h-3.5 text-rose-600" />
                      <span className="text-[10px] uppercase tracking-[0.14em] text-rose-800 font-semibold">Risk flags</span>
                      <Badge variant="outline" className="text-[10px] bg-rose-100 text-rose-800 border-rose-300">{overview.risk_flags.length}</Badge>
                    </div>
                    <ul className="space-y-2">
                      {overview.risk_flags.map((r, i) => (
                        <li key={i} className="text-sm">
                          <span className="text-[10px] uppercase tracking-[0.12em] text-rose-700 font-medium mr-2">{r.type?.replaceAll("_", " ")}</span>
                          <span className="text-slate-700">{r.note}</span>
                        </li>
                      ))}
                    </ul>
                  </Card>
                )}
              </>
            )}
          </TabsContent>

          <TabsContent value="timeline" className="mt-4">
            <ul className="space-y-2">
              {calls.map(c => (
                <li key={c.id}>
                  <Link to={`/fathom/calls/${c.id}`} data-testid={`program-call-${c.id}`}>
                    <Card className="p-3 hover:shadow-sm hover:border-slate-300 transition-all">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-slate-900 truncate">{c.meeting_title}</div>
                          <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                            <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {c.meeting_at && new Date(c.meeting_at).toLocaleString()}</span>
                            <span className="flex items-center gap-1"><User className="w-3 h-3" /> {c.sales_rep_name || "—"}</span>
                            {(c.action_item_count || 0) > 0 && <span>{c.action_item_count} action items</span>}
                            {c.risk_flag_count > 0 && (
                              <span className="text-rose-700 flex items-center gap-1">
                                <AlertTriangle className="w-3 h-3" /> {c.risk_flag_count}
                              </span>
                            )}
                          </div>
                          {c.summary && <p className="text-xs text-slate-600 mt-1 line-clamp-2">{c.summary.slice(0, 240)}</p>}
                        </div>
                        {c.recording_url && (
                          <a href={c.recording_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="flex-shrink-0">
                            <Button variant="ghost" size="sm"><ExternalLink className="w-3.5 h-3.5" /></Button>
                          </a>
                        )}
                      </div>
                    </Card>
                  </Link>
                </li>
              ))}
            </ul>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

function IntelSection({ label, items, accent }) {
  if (!items || items.length === 0) return null;
  const toneClass = accent === "amber" ? "border-amber-100 bg-amber-50/40" : "border-slate-200 bg-white";
  return (
    <Card className={`p-3 ${toneClass}`}>
      <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 mb-1.5 font-medium">{label}</div>
      <ul className="space-y-1 text-sm text-slate-700">
        {items.map((x, i) => (
          <li key={i} className="flex items-start gap-2"><span className="w-1 h-1 mt-2 rounded-full bg-slate-400 flex-shrink-0"></span>{typeof x === "string" ? x : (x.text || x.note || JSON.stringify(x))}</li>
        ))}
      </ul>
    </Card>
  );
}
